import React from 'react';
import type { Root } from 'react-dom/client';
import { createRoot } from 'react-dom/client';
import { SaveChangesModal } from '../../shared/components';
import store from '../../store';

type AnswerType = 'save' | 'discard';

let promptRoot: Root | null = null;

const unmountPromptRoot = (): void => {
  if (promptRoot) {
    promptRoot.unmount();
    promptRoot = null;
  }
};

const unmountCurrentPromptRoot = (root: Root): void => {
  if (promptRoot === root) {
    root.unmount();
    promptRoot = null;
  }
};

const waitForChangesHandled = (
  answer: AnswerType,
  callback: (answer: boolean) => void,
): void => {
  let completed = false;
  let unsubscribe: (() => void) | null = null;

  const completeIfReady = (): void => {
    if (completed || store.getState().common.hasUnsubmittedData) {
      return;
    }

    completed = true;

    if (unsubscribe) {
      unsubscribe();
    }

    callback(true);
  };

  unsubscribe = store.subscribe(completeIfReady);

  window.dispatchEvent(new CustomEvent(`${answer}-changes`));

  completeIfReady();
};

export const promptCallback = (
  _: unknown,
  callback: (answer: boolean) => void,
): void => {
  const node = document.getElementById('custom-prompt');

  if (!node) {
    callback(false);

    return;
  }

  unmountPromptRoot();

  const root = createRoot(node);

  promptRoot = root;

  const onButtonClick = (answer: AnswerType): void => {
    unmountCurrentPromptRoot(root);
    waitForChangesHandled(answer, callback);
  };

  root.render(
    <SaveChangesModal
      visible
      cancelButtonProps={{
        onClick: (): void => onButtonClick('discard'),
      }}
      onCancel={(): void => {
        unmountCurrentPromptRoot(root);
        callback(false);
      }}
      onOk={(): void => onButtonClick('save')}
    />,
  );
};
